import React, { useEffect, useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { updateSlot, fetchGymSlots } from '../../redux/slotSlice';
import { RootState, AppDispatch } from '../../redux/store';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';

export const EditSlotPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { slotId } = useParams<{ slotId: string }>();
  const [searchParams] = useSearchParams();
  const gymId = searchParams.get('gymId');
  
  const { slots, isLoading, error } = useSelector((state: RootState) => state.slot);
  const [successMessage, setSuccessMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState({
    capacity: '',
    price_coins: '',
    price_cash: '',
    is_active: true
  });

  useEffect(() => {
    if (gymId && slotId) {
      dispatch(fetchGymSlots({ gymId: parseInt(gymId) }));
    }
  }, [dispatch, gymId, slotId]);

  useEffect(() => {
    if (slotId && slots.length > 0) {
      const slot = slots.find((s) => s.id === parseInt(slotId));
      if (slot) {
        setFormData({
          capacity: slot.capacity.toString(),
          price_coins: slot.price_coins.toString(),
          price_cash: slot.price_cash.toString(),
          is_active: slot.is_active
        });
      }
    }
  }, [slotId, slots]);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
    if (fieldErrors[field]) {
      setFieldErrors({ ...fieldErrors, [field]: '' });
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    const capacity = parseInt(formData.capacity);
    if (!formData.capacity || isNaN(capacity) || capacity < 1) {
      errors.capacity = 'Capacity must be at least 1';
    }

    const priceCoins = parseInt(formData.price_coins);
    if (!formData.price_coins || isNaN(priceCoins) || priceCoins < 0) {
      errors.price_coins = 'Price in coins is required';
    }

    const priceCash = parseFloat(formData.price_cash);
    if (!formData.price_cash || isNaN(priceCash) || priceCash < 0) {
      errors.price_cash = 'Price in cash is required';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !gymId || !slotId) {
      return;
    }

    const result = await dispatch(
      updateSlot({
        gymId: parseInt(gymId),
        slotId: parseInt(slotId),
        data: {
          capacity: parseInt(formData.capacity),
          price_coins: parseInt(formData.price_coins),
          price_cash: parseFloat(formData.price_cash),
          is_active: formData.is_active
        }
      })
    );

    if (result.type === updateSlot.fulfilled.type) {
      setSuccessMessage('Slot updated successfully!');
      setTimeout(() => navigate('/slots'), 1500);
    }
  };

  const currentSlot = slots.find((s) => s.id === parseInt(slotId || '0'));

  if (isLoading && !currentSlot) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: '#14B8A6' }} />
      </Box>
    );
  }

  if (!currentSlot && !isLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">Slot not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/slots')}
          sx={{ mt: 2 }}
        >
          Back to Slots
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/slots')}
          sx={{ mb: 2 }}
        >
          Back to Slots
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
          Edit Slot
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Update slot capacity, pricing, and status
        </Typography>
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={4}>
          {/* Read-only Slot Info */}
          {currentSlot && (
            <Grid item xs={12}>
              <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                  Slot Information (Read-only)
                </Typography>
                <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                      Date
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {new Date(currentSlot.date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                      Time
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentSlot.start_time.substring(0, 5)} - {currentSlot.end_time.substring(0, 5)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                      Current Bookings
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentSlot.booked_count} / {currentSlot.capacity}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ color: '#999', mb: 0.5 }}>
                      Occupancy
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {currentSlot.occupancy_percentage || 0}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}

          {/* Editable Fields */}
          <Grid item xs={12}>
            <Box sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Update Slot Details
              </Typography>
              <Grid container spacing={{ xs: 3, sm: 3, md: 3.5 }} sx={{ '& .MuiGrid-item': { paddingTop: '24px !important' } }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Capacity"
                    type="number"
                    value={formData.capacity}
                    onChange={(e) => handleChange('capacity', e.target.value)}
                    required
                    error={!!fieldErrors.capacity}
                    helperText={fieldErrors.capacity || 'Total number of available seats'}
                    inputProps={{ min: currentSlot?.booked_count || 1 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status"
                    select
                    value={formData.is_active.toString()}
                    onChange={(e) => handleChange('is_active', e.target.value === 'true')}
                    SelectProps={{ native: true }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price (Coins)"
                    type="number"
                    value={formData.price_coins}
                    onChange={(e) => handleChange('price_coins', e.target.value)}
                    required
                    error={!!fieldErrors.price_coins}
                    helperText={fieldErrors.price_coins}
                    inputProps={{ min: 0 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Price (Cash ₹)"
                    type="number"
                    value={formData.price_cash}
                    onChange={(e) => handleChange('price_cash', e.target.value)}
                    required
                    error={!!fieldErrors.price_cash}
                    helperText={fieldErrors.price_cash}
                    inputProps={{ min: 0, step: '0.01' }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Action Buttons */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/slots')}
                sx={{ height: '48px', minWidth: { xs: '100%', sm: '140px' }, order: { xs: 2, sm: 1 } }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <SaveIcon />}
                sx={{
                  height: '48px',
                  minWidth: { xs: '100%', sm: '160px' },
                  backgroundColor: '#14B8A6',
                  '&:hover': {
                    backgroundColor: '#0F9688'
                  },
                  order: { xs: 1, sm: 2 }
                }}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default EditSlotPage;
